/**
 * Maps a `SavedTemplate` (UI model) to a `RotaTemplate` (rotagen contract).
 *
 * Now that the wizard edits and persists contract shapes directly, this is a
 * thin assembly: the shifts' `schedule`/`staffing` and the template's `scope`
 * and `schedulingRules` are already contract-native. The work is dropping the
 * wizard-only `ShiftSet` fields (`name`, `workArea`), resolving the canonical
 * `shiftType` name from the catalogue, validating times, deriving the contract
 * `baseline` from the UI `demandScaling` (so scaling-enabled shifts pass
 * `RotaTemplateSchema`'s metric-coverage check and rotagen can scale them), and
 * running the result through `RotaTemplateSchema`.
 */

import {
  RotaTemplateSchema,
  type Identifiable,
  type RotaTemplate,
  type ShiftPattern,
} from '@repo/contracts';

import { baselineFromDemandScaling } from '@/domain/templates/demand-scaling';
import type { SavedTemplate } from '@/types/saved-template';
import type { ShiftSet, ShiftType } from '@/types/template';

/**
 * Maps a SavedTemplate (UI model) to a RotaTemplate (contract format), validated
 * against `RotaTemplateSchema`.
 *
 * @param template - The SavedTemplate from the UI.
 * @param shiftTypes - Shift types used to resolve canonical shiftType names by id.
 * @throws Error if the mapped template fails Zod schema validation.
 */
export function mapSavedTemplateToRotaTemplate(
  template: SavedTemplate,
  shiftTypes: ShiftType[],
): RotaTemplate {
  // Derive the contract baseline from the UI demand-scaling settings. Omitted
  // (undefined) when scaling is off or no positive figure is set — scaling
  // shifts then fail the schema's metric-coverage check, exactly as intended.
  const baseline = baselineFromDemandScaling(template.demandScaling);

  const mappedTemplate: RotaTemplate = {
    id: template.id,
    name: template.name,
    description: template.description ?? '',
    scope: template.scope,
    ...(baseline ? { baseline } : {}),
    shifts: template.shifts.map((shift) => mapShiftSetToShiftPattern(shift, shiftTypes)),
    schedulingRules: template.schedulingRules,
  };

  return validateRotaTemplate(mappedTemplate);
}

/**
 * Validates a RotaTemplate against the Zod schema. Logs detailed issues for
 * debugging but throws a user-friendly message.
 */
function validateRotaTemplate(template: RotaTemplate): RotaTemplate {
  const result = RotaTemplateSchema.safeParse(template);

  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');
    console.error(`RotaTemplate validation failed:\n${issues}`);

    throw new Error(
      'Template validation failed. Please check your template configuration and try again.',
    );
  }

  return result.data;
}

/** Regular expression for validating HH:mm time format (00:00–23:59). */
const TIME_FORMAT_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * Maps a single `ShiftSet` to a contract `ShiftPattern`, dropping the wizard-only
 * `name`/`workArea` and resolving the canonical `shiftType` name.
 *
 * @throws Error if startTime or endTime is not in HH:mm format.
 */
function mapShiftSetToShiftPattern(shift: ShiftSet, shiftTypes: ShiftType[]): ShiftPattern {
  validateTimeFormat(shift.startTime, 'startTime', shift.id);
  validateTimeFormat(shift.endTime, 'endTime', shift.id);

  return {
    id: shift.id,
    schedule: shift.schedule,
    shiftType: mapShiftType(shift.shiftType, shiftTypes, shift.id),
    startTime: shift.startTime,
    endTime: shift.endTime,
    staffing: shift.staffing,
  };
}

/** Validates that a time string is in HH:mm format and throws if invalid. */
function validateTimeFormat(time: string, fieldName: string, shiftId: string): void {
  if (!TIME_FORMAT_REGEX.test(time)) {
    throw new Error(
      `Invalid ${fieldName} format for shift ${shiftId}: "${time}". Expected HH:mm format (00:00-23:59).`,
    );
  }
}

/**
 * Resolves a ShiftSet's shiftType to the contract `Identifiable`, looking up the
 * canonical name from the catalogue and falling back to the stored snapshot.
 */
function mapShiftType(
  shiftType: Identifiable,
  shiftTypes: ShiftType[],
  shiftId: string,
): Identifiable {
  const resolved = shiftTypes.find((st) => st.id === shiftType.id);
  if (!resolved) {
    console.warn(`mapShiftType: no match for shiftType.id "${shiftType.id}" on shift ${shiftId}`);
  }

  return {
    id: resolved?.id ?? shiftType.id,
    name: resolved?.name ?? shiftType.name,
  };
}
